import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { Modal, Typography } from 'antd';
import { type FC, useEffect, useRef, useState } from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import { isAndroidApp, isInApp, requestAppLocationPermission } from '../appLocationPermission';
import { t } from '../i18n';
import { GeolocationStatus, useStore } from '../store';

const { Text } = Typography;

type Prompt = 'explain' | 'denied' | null;

export const OwnLocationManager: FC = () => {
  const watchId = useRef<number | null>(null);
  const retryTimer = useRef<number | null>(null);
  const mounted = useRef(true);
  const geolocationStatus = useStore((state) => state.timer.geolocation.status);
  const setGeolocationStatus = useStore((state) => state.actions.setGeolocationStatus);
  const setOwnLocation = useStore((state) => state.actions.setOwnLocation);
  const [prompt, setPrompt] = useState<Prompt>(null);

  function isSearching() {
    return (
      mounted.current &&
      useStore.getState().timer.geolocation.status === GeolocationStatus.SEARCHING
    );
  }

  function startWatching({ retryOnDenied = false } = {}) {
    stopWatching();
    watchId.current = startGeolocationWatcher({
      setGeolocationStatus,
      setOwnLocation,
      onDenied: () => {
        if (retryOnDenied) {
          // The app enables geolocation in its WebView only after it has re-rendered
          retryTimer.current = window.setTimeout(() => {
            retryTimer.current = null;
            if (isSearching()) startWatching();
          }, OneSecondInMillis);
          return;
        }
        handleDenied();
      },
    });
  }

  function stopWatching() {
    if (retryTimer.current != null) {
      window.clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    if (watchId.current == null) return;
    window.navigator.geolocation.clearWatch(watchId.current);
    watchId.current = null;
  }

  function handleDenied() {
    stopWatching();
    setGeolocationStatus(GeolocationStatus.OFF);
    setOwnLocation(null);
    setPrompt('denied');
  }

  async function initGeolocation() {
    if (geolocationStatus !== GeolocationStatus.SEARCHING || watchId.current != null) return;

    const permission = await checkGeolocationPermission();
    if (!isSearching()) return;
    if (permission === 'granted') {
      startWatching();
      return;
    }
    // Store review: the OS permission dialog MUST follow an explanation
    setPrompt('explain');
  }

  async function handleOk() {
    setPrompt(null);
    if (!isAndroidApp()) {
      startWatching();
      return;
    }
    const granted = await requestAppLocationPermission();
    if (!isSearching()) return;
    if (granted === false) {
      handleDenied();
      return;
    }
    // null: an app version without the handler; the WebView decides alone
    startWatching({ retryOnDenied: granted === true });
  }

  function handleCancel() {
    setPrompt(null);
    setGeolocationStatus(GeolocationStatus.OFF);
  }

  useEffect(() => {
    if (geolocationStatus === GeolocationStatus.OFF && watchId.current != null) {
      stopWatching();
      setOwnLocation(null);
    }

    initGeolocation();
  }, [geolocationStatus]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stopWatching();
    };
  }, []);

  return (
    <>
      <Modal
        title={t('ownlocation.permission.title')}
        open={prompt === 'explain'}
        onCancel={handleCancel}
        centered
        onOk={handleOk}
      >
        <Text>{t('ownlocation.permission.message')}</Text>
      </Modal>
      <Modal
        title={t('ownlocation.denied.title')}
        open={prompt === 'denied'}
        onCancel={() => setPrompt(null)}
        onOk={() => setPrompt(null)}
        footer={(_, { OkBtn }) => <OkBtn />}
        centered
      >
        <Text>{t(isInApp() ? 'ownlocation.denied.app' : 'ownlocation.denied.browser')}</Text>
      </Modal>
    </>
  );
};

async function checkGeolocationPermission(): Promise<PermissionState> {
  // Inside the app the WebView answers, not the phone's setting for the app
  if (isInApp() || navigator.permissions == null) return 'prompt';

  try {
    const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
    return permissionStatus.state;
  } catch (error) {
    console.error('Error checking geolocation permission:', error);
    return 'prompt';
  }
}

const startGeolocationWatcher = ({
  setGeolocationStatus,
  setOwnLocation,
  onDenied,
}: {
  setGeolocationStatus: (status: GeolocationStatus) => void;
  setOwnLocation: (location: { lat: number; lng: number; opacity: number } | null) => void;
  onDenied: () => void;
}): number => {
  return window.navigator.geolocation.watchPosition(
    (position) => {
      if (position != null && position.coords != null) {
        const latlng = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          opacity: 1,
        };
        setGeolocationStatus(GeolocationStatus.FOLLOWING);
        setOwnLocation(latlng);
        trackEvent('own_location', { result: 'success' });
      } else {
        trackEvent('own_location', { result: 'fail' });
      }
    },
    (error) => {
      trackEvent('own_location', { result: 'fail' });
      // Timeouts and a missing fix are common with weak reception; the watch keeps trying
      if (error.code === error.PERMISSION_DENIED) onDenied();
    },
    {
      enableHighAccuracy: true,
      maximumAge: 30 * OneSecondInMillis,
      timeout: 30 * OneSecondInMillis,
    },
  );
};
